import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen image viewer. Pass a single `src`, or `images` (array of
 * { src, alt, downloadName }) plus `index` to browse a whole note with
 * arrow keys, on-screen chevrons or a swipe.
 */
export default function ImageLightbox({ open, src, alt, downloadName, images, index = 0, onClose }) {
  const list = images?.length ? images : src ? [{ src, alt, downloadName }] : [];
  const [cur, setCur] = useState(index);
  const touchRef = useRef(null);

  useEffect(() => { if (open) setCur(Math.min(Math.max(index, 0), Math.max(list.length - 1, 0))); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, index, list.length]);

  const go = useCallback((dir) => {
    setCur((c) => (list.length ? (c + dir + list.length) % list.length : 0));
  }, [list.length]);

  useEffect(() => {
    if (!open) return;
    // Capture phase + stopPropagation so Esc/arrows never reach a parent
    // dialog (e.g. the Media panel sheet) and close it behind us.
    const onKey = (e) => {
      if (!["Escape", "ArrowRight", "ArrowLeft"].includes(e.key)) return;
      e.stopPropagation();
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, go]);

  if (!open || !list.length) return null;
  const photo = list[Math.min(cur, list.length - 1)];
  const many = list.length > 1;

  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (start == null) return;
    const dx = e.changedTouches[0].clientX - start;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
  };

  return createPortal(
    <div
      data-testid="image-lightbox"
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-150"
      // A parent Radix dialog (Media panel) sets body { pointer-events: none }
      // while it's open; re-enable it for the viewer so the chevrons work.
      style={{ pointerEvents: "auto" }}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <a
          href={photo.src}
          download={photo.downloadName || undefined}
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-download-btn"
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
          aria-label="Download image"
          title="Download"
        >
          <Download className="h-5 w-5" />
        </a>
        <a
          href={photo.src}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-openinnew-btn"
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
          aria-label="Open in new tab"
          title="Open in new tab"
        >
          <ExternalLink className="h-5 w-5" />
        </a>
        <button
          type="button"
          onClick={onClose}
          data-testid="lightbox-close-btn"
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
          aria-label="Close"
          title="Close (Esc)"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {many && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            data-testid="lightbox-prev-btn"
            aria-label="Previous photo"
            className="absolute left-2 sm:left-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-all"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            data-testid="lightbox-next-btn"
            aria-label="Next photo"
            className="absolute right-2 sm:right-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-all"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <div
            data-testid="lightbox-counter"
            className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 rounded-full bg-white/10 px-3 py-1 text-xs text-white tabular-nums"
          >
            {cur + 1} / {list.length}
          </div>
        </>
      )}

      <img
        key={photo.src}
        src={photo.src}
        alt={photo.alt || "image"}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] max-w-[94vw] object-contain select-none rounded-md shadow-2xl animate-in fade-in duration-150"
        draggable={false}
      />
    </div>,
    document.body,
  );
}
